import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const UserDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [bookingPage, setBookingPage] = useState(1);
  const bookingsPerPage = 10;

  useEffect(() => {
    const adminData = localStorage.getItem("adminData");
    if (adminData) {
      setAdmin(JSON.parse(adminData));
    }
  }, []);

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}admin/users/${id}/details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user details from server");
      }

      const data = await response.json();
      const raw = data.data || data;
      const personal = raw.personalInfo || raw.user || raw;
      const status = raw.statusInfo || {};
      const stats = raw.statistics || {};

      setSelectedUser({
        ...personal,
        isVerified: status.isVerified !== undefined ? status.isVerified : personal.isVerified,
        isSuspended: status.isSuspended !== undefined ? status.isSuspended : personal.isSuspended,
        totalBookings: stats.bookings?.total || personal.totalBookings || 0,
        averageRating: status.averageRating !== undefined ? status.averageRating : personal.averageRating || 0,
        createdAt: personal.createdAt,
      });

      setUserDetails({
        business: {
          totalBookings: stats.bookings?.total || 0,
          totalRevenue: stats.spending?.total || 0,
          averageRevenuePerUser: stats.spending?.averagePerBooking || 0,
        },
        ...raw,
      });
    } catch (err) {
      console.error("Error fetching user details:", err);
      setError(err.message || "Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminData");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Sidebar admin={admin} onLogout={handleLogout} />
        <main className="lg:ml-72 min-h-screen">
          <div className="flex flex-col items-center justify-center py-32">
            <div className="animate-spin rounded-full h-12 w-12 border-3 border-stone-200 border-t-[#F8D346] mb-4"></div>
            <span className="text-xs font-black text-stone-600 uppercase tracking-wider">
              Loading user profile & intelligence...
            </span>
          </div>
        </main>
      </div>
    );
  }

  if (error || !selectedUser) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Sidebar admin={admin} onLogout={handleLogout} />
        <main className="lg:ml-72 min-h-screen">
          <div className="p-4 lg:p-8 pt-16 lg:pt-8">
            <button
              onClick={() => navigate("/admin/users")}
              className="inline-flex items-center gap-2 bg-white border border-stone-200 text-stone-700 hover:text-stone-950 hover:border-[#F8D346] hover:bg-[#F8D346]/10 px-4 py-2 rounded-xl text-xs font-black shadow-2xs transition-all mb-6"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Users Directory
            </button>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-900 max-w-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="font-black text-sm">Unable to Load User</h3>
              </div>
              <p className="text-xs font-semibold text-rose-700 ml-11">
                {error || "User record not found or inaccessible."}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const initial = (selectedUser.name?.trim()?.charAt(0) || "U").toUpperCase();
  const ratingVal = selectedUser.averageRating ? Number(selectedUser.averageRating).toFixed(1) : "0.0";
  const businessStats = userDetails?.business || {};
  const allBookings = userDetails?.allBookings || userDetails?.recentBookings || [];
  const totalBookingPages = Math.ceil(allBookings.length / bookingsPerPage) || 1;
  const paginatedBookings = allBookings.slice(
    (bookingPage - 1) * bookingsPerPage,
    bookingPage * bookingsPerPage
  );
  const topServices = userDetails?.topServices || [];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Sidebar admin={admin} onLogout={handleLogout} />

      <main className="lg:ml-72 min-h-screen">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-md border-b border-stone-200/80 px-4 lg:px-8 py-5 sticky top-0 z-40 shadow-xs">
          <div className="pt-14 lg:pt-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate("/admin/users")}
                  className="inline-flex items-center gap-1.5 bg-white border border-stone-200 text-stone-700 hover:text-stone-950 hover:border-[#F8D346] hover:bg-[#F8D346]/10 px-3 py-2 rounded-xl text-xs font-black shadow-2xs transition-all shrink-0"
                  title="Return to Users"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span>Directory</span>
                </button>

                <div className="h-6 w-px bg-stone-200" />

                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="w-2 h-2 rounded-full bg-[#F8D346] animate-pulse" />
                    <span className="text-[10px] font-black text-amber-800 uppercase tracking-wider">
                      User Profile Record
                    </span>
                  </div>
                  <h1 className="text-lg lg:text-xl font-black text-stone-950 tracking-tight leading-tight flex items-center gap-2">
                    <span>{selectedUser.name || "User Details"}</span>
                    {selectedUser.isVerified && (
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200/80 px-2 py-0.5 rounded-full text-[10px] font-black">
                        ✓ Verified
                      </span>
                    )}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs font-bold text-stone-400 bg-stone-100/80 px-3 py-1.5 rounded-xl border border-stone-200/60 self-start sm:self-auto">
                <span>UID:</span>
                <span className="font-mono text-stone-800 text-[11px] font-black">
                  {selectedUser.id || id}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-4 lg:p-8 space-y-6">
          {/* Main Profile Identity Card */}
          <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Avatar Circle */}
              <div className="relative shrink-0">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-stone-800 bg-stone-900 text-[#F8D346] flex items-center justify-center shadow-md">
                  {selectedUser.profileImage ? (
                    <img
                      src={selectedUser.profileImage}
                      alt={selectedUser.name || "User"}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <span className="font-black text-3xl text-[#F8D346]">
                      {initial}
                    </span>
                  )}
                </div>
                {selectedUser.isVerified && (
                  <span
                    className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-black ring-3 ring-white shadow-xs"
                    title="Verified User"
                  >
                    ✓
                  </span>
                )}
              </div>

              {/* User Bio & Contact Details */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h2 className="text-2xl font-black text-stone-950 tracking-tight">
                    {selectedUser.name || "Unnamed User"}
                  </h2>

                  <div className="flex items-center gap-1.5">
                    {selectedUser.isVerified ? (
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200/80 px-2.5 py-0.5 rounded-full text-xs font-black">
                        ✓ Verified Account
                      </span>
                    ) : (
                      <span className="bg-amber-50 text-amber-800 border border-amber-200/80 px-2.5 py-0.5 rounded-full text-xs font-black">
                        Unverified
                      </span>
                    )}

                    {selectedUser.isSuspended && (
                      <span className="bg-rose-50 text-rose-700 border border-rose-200/80 px-2.5 py-0.5 rounded-full text-xs font-black">
                        Restricted
                      </span>
                    )}
                  </div>
                </div>

                {/* Contact grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-4 text-xs">
                  <div className="p-3 rounded-xl bg-stone-50 border border-stone-100 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-stone-200/80 text-stone-700 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-stone-400 uppercase">Phone Number</span>
                      <p className="font-black text-stone-900 text-xs">{selectedUser.phone || "N/A"}</p>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-stone-50 border border-stone-100 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-stone-200/80 text-stone-700 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="truncate">
                      <span className="text-[10px] font-bold text-stone-400 uppercase">Email Address</span>
                      <p className="font-black text-stone-900 text-xs truncate">{selectedUser.email || "N/A"}</p>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-stone-50 border border-stone-100 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-stone-200/80 text-stone-700 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-stone-400 uppercase">City & Location</span>
                      <p className="font-black text-stone-900 text-xs">{selectedUser.city || selectedUser.address || "N/A"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3 Executive Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* 1. Spending Metrics */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm p-5 lg:p-6 flex flex-col justify-between">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                  Total Spending
                </span>
                <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-900 border border-amber-200/70 flex items-center justify-center">
                  <span className="text-sm font-black">₹</span>
                </div>
              </div>

              <div>
                <div className="text-3xl font-black text-stone-900 tracking-tight">
                  ₹{(businessStats.totalRevenue || 0).toLocaleString()}
                </div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-400 font-semibold">Avg / Booking</span>
                  <span className="font-black text-stone-800">
                    ₹{businessStats.averageRevenuePerUser || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* 2. Booking Volume */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm p-5 lg:p-6 flex flex-col justify-between">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                  Bookings Completed
                </span>
                <div className="w-9 h-9 rounded-xl bg-stone-900 text-[#F8D346] border border-stone-800 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>

              <div>
                <div className="text-3xl font-black text-stone-900 tracking-tight">
                  {selectedUser.totalBookings || businessStats.totalBookings || 0}
                </div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-400 font-semibold">Platform Rating</span>
                  <span className="font-black text-amber-900 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200/60">
                    ★ {ratingVal} / 5.0
                  </span>
                </div>
              </div>
            </div>

            {/* 3. Member Tenure */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm p-5 lg:p-6 flex flex-col justify-between">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-stone-500">
                  Account Standing
                </span>
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200/70 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              <div>
                <div className="text-lg font-black text-stone-900 tracking-tight">
                  {selectedUser.isSuspended ? "Restricted" : "Active & Good Standing"}
                </div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-400 font-semibold">Member Since</span>
                  <span className="font-black text-stone-700">
                    {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Services Preferences */}
          {topServices.length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm p-6">
              <h3 className="text-sm font-black text-stone-950 uppercase tracking-wider mb-4 flex items-center gap-2">
                <span>Top Utilized Services</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {topServices.map((srv, idx) => (
                  <div key={idx} className="p-3 rounded-xl bg-stone-50 border border-stone-200/60 flex items-center justify-between">
                    <span className="font-black text-stone-800 text-xs truncate mr-2">{srv.service}</span>
                    <span className="bg-[#F8D346]/20 text-amber-950 px-2 py-0.5 rounded-full text-[11px] font-black border border-[#F8D346]/40 shrink-0">
                      {srv.bookings} bookings
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Booking History Feed */}
          {allBookings.length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm overflow-hidden mb-6">
              <div className="p-5 lg:p-6 border-b border-stone-100 flex flex-wrap items-center justify-between gap-3 bg-stone-50/40">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-black text-stone-950 tracking-tight">
                      Booking History
                    </h3>
                    <span className="bg-[#F8D346]/20 text-amber-950 px-2.5 py-0.5 rounded-full text-[11px] font-black border border-[#F8D346]/40">
                      {allBookings.length} Total Records
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 font-medium mt-0.5">
                    Complete reservation and service fulfillment records for this user
                  </p>
                </div>

                <div className="text-xs font-bold text-stone-400 bg-white border border-stone-200/80 px-3 py-1.5 rounded-xl shadow-2xs">
                  Page {bookingPage} of {totalBookingPages}
                </div>
              </div>

              <div className="divide-y divide-stone-100 overflow-x-auto">
                {paginatedBookings.map((booking) => (
                  <div key={booking.id} className="p-4 lg:p-5 hover:bg-stone-50/60 transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-black text-stone-900 text-xs">
                          {booking.service?.name || "Service"}
                        </span>
                        {booking.subService?.name && (
                          <span className="text-stone-400 text-xs font-semibold">
                            • {booking.subService.name}
                          </span>
                        )}
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-md border ${
                          booking.status === "COMPLETED"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200/70"
                            : booking.status === "CANCELLED"
                            ? "bg-rose-50 text-rose-700 border-rose-200/70"
                            : "bg-amber-50 text-amber-800 border-amber-200/70"
                        }`}>
                          {booking.status}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-xs text-stone-500">
                        <span>Provider: <strong className="text-stone-700 font-bold">{booking.provider?.name || "N/A"}</strong></span>
                        <span>•</span>
                        <span>ID: <code className="font-mono text-[11px] text-stone-600">{booking.id}</code></span>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className="text-sm font-black text-stone-900">
                        ₹{(booking.finalPrice || booking.resolvedPrice || 0).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-stone-400 font-medium">
                        {booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalBookingPages > 1 && (
                <div className="p-4 lg:p-5 border-t border-stone-200/80 bg-stone-50/40 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="text-xs font-bold text-stone-500">
                    Showing {(bookingPage - 1) * bookingsPerPage + 1} to{" "}
                    {Math.min(bookingPage * bookingsPerPage, allBookings.length)} of {allBookings.length} bookings
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => setBookingPage((prev) => Math.max(prev - 1, 1))}
                      disabled={bookingPage === 1}
                      className={`px-3 py-1.5 rounded-xl border text-xs font-black transition-all ${
                        bookingPage === 1
                          ? "border-stone-200 text-stone-300 cursor-not-allowed bg-white"
                          : "border-stone-200 bg-white text-stone-700 hover:border-[#F8D346] hover:bg-[#F8D346]/10 shadow-2xs"
                      }`}
                    >
                      ← Previous
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, totalBookingPages) }, (_, i) => {
                      let pageNum;
                      if (totalBookingPages <= 5) {
                        pageNum = i + 1;
                      } else if (bookingPage <= 3) {
                        pageNum = i + 1;
                      } else if (bookingPage >= totalBookingPages - 2) {
                        pageNum = totalBookingPages - 4 + i;
                      } else {
                        pageNum = bookingPage - 2 + i;
                      }

                      const isActive = bookingPage === pageNum;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setBookingPage(pageNum)}
                          className={`w-8 h-8 rounded-xl border text-xs font-black transition-all flex items-center justify-center ${
                            isActive
                              ? "bg-gradient-to-r from-[#F8D346] to-[#F5C518] text-stone-950 border-[#F5C518] shadow-xs shadow-amber-300/30"
                              : "border-stone-200 bg-white text-stone-700 hover:bg-stone-100/80 shadow-2xs"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {/* Next Button */}
                    <button
                      onClick={() => setBookingPage((prev) => Math.min(prev + 1, totalBookingPages))}
                      disabled={bookingPage === totalBookingPages}
                      className={`px-3 py-1.5 rounded-xl border text-xs font-black transition-all ${
                        bookingPage === totalBookingPages
                          ? "border-stone-200 text-stone-300 cursor-not-allowed bg-white"
                          : "border-stone-200 bg-white text-stone-700 hover:border-[#F8D346] hover:bg-[#F8D346]/10 shadow-2xs"
                      }`}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserDetailsPage;
